import { hideVisually, readableColor } from 'polished';
import styled, { css } from 'styled-components';
import { switchProp } from 'styled-tools';
import {
  black,
  blue,
  grey20,
  grey60,
  ui_black,
  ui_white,
  white
} from 'Styles/colors';
import { fontBody } from 'Styles/fonts';
import strikethrough from 'Styles/strikethrough';

import breakpoint from 'Styles/breakpoints';
import { isHexCode } from '../../util/util';

/**
 * There are some style differences when the swatch style
 * reaches a certain size threshold
 */
const SIZE_BREAKPOINTS = {
  small: 16,
  medium: 24
};

const getRadioSizeStyles = (sizeVar) => {
  let borderWidth = 2;
  let dimension = sizeVar + 10;
  if (sizeVar <= SIZE_BREAKPOINTS.small) {
    borderWidth = 1;
    dimension = sizeVar + 6;
  } else if (sizeVar <= SIZE_BREAKPOINTS.medium) {
    borderWidth = 1;
    dimension = sizeVar + 6;
  }
  return [borderWidth, dimension];
};

export const Input = styled.input`
  opacity: 0;
  position: absolute;
  left: -9999px;
`;

const getReadableColorForColorSwatch = (isColorSwatch, label) => {
  /**
   * Ensures the colorSwatch is filled with an 'color' image used in `FilterMenuOption` and has a non-empty label.
   * The color for Icon or Text inside the swatch is selected based on the label's color to enhance readability.
   *
   * By default, we return white.
   *
   */
  if (!isColorSwatch || !label) return white;
  const $readableColor = readableColor(
    label.toLowerCase(),
    ui_white,
    ui_black,
    true
  );
  return $readableColor;
};

export const Swatch = styled.div.attrs(({ 
  fill, 
  disabled, 
  size, 
  $responsiveSize, 
  hasMargin,
  isSelected,
  type,
  $styleQueryMode 
}) => {
  // Calculate responsive size
  const resSize = $responsiveSize || {
    small: size,
    medium: size,
    large: size
  };

  // Calculate radio dimensions
  const [smallBorderWidth, smallDimension] = getRadioSizeStyles(resSize.small);
  const [mediumBorderWidth, mediumDimension] = getRadioSizeStyles(resSize.medium);
  const [largeBorderWidth, largeDimension] = getRadioSizeStyles(resSize.large);

  // Determine background style
  const isHex = isHexCode(fill);
  const backgroundColor = isHex ? (fill.toLowerCase().includes('ffffff') ? grey20 : fill) : 'transparent';
  const backgroundImage = isHex 
    ? (disabled ? strikethrough(readableColor(fill, grey20, black, true)) : 'none')
    : disabled 
      ? `${strikethrough(grey20)},url(${fill})`
      : `url(${fill})`;

  return {
    style: {
      // Use CSS custom properties instead of direct style values
      '--swatch-size-small': `${resSize.small}px`,
      '--swatch-size-medium': `${resSize.medium}px`,
      '--swatch-size-large': `${resSize.large}px`,
      '--swatch-margin': hasMargin ? '6px' : '0',
      '--swatch-bg-color': isHex ? backgroundColor : 'transparent',
      '--swatch-bg-image': !isHex ? backgroundImage : (disabled ? backgroundImage : 'none'),
      '--swatch-bg-size': !isHex ? 'cover' : 'auto',
      '--swatch-color': isHex ? readableColor(fill, white, black, true) : 'inherit',
      '--swatch-cursor': disabled ? 'not-allowed' : 'pointer',
      '--swatch-border-small': `${smallBorderWidth}px`,
      '--swatch-border-medium': `${mediumBorderWidth}px`,
      '--swatch-border-large': `${largeBorderWidth}px`,
      '--swatch-dimension-small': `${smallDimension}px`,
      '--swatch-dimension-medium': `${mediumDimension}px`,
      '--swatch-dimension-large': `${largeDimension}px`,
      '--swatch-selected-opacity': isSelected ? 1 : 0,
      '--swatch-selected-scale': isSelected ? 1 : 0.7
    },
    'data-is-radio': type === 'radio',
    'data-style-query-mode': $styleQueryMode
  };
})`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  z-index: 0;
  
  /* Use CSS custom properties for dynamic styles */
  width: var(--swatch-size-small);
  height: var(--swatch-size-small);
  margin: var(--swatch-margin);
  background-color: var(--swatch-bg-color);
  background-image: var(--swatch-bg-image);
  background-size: var(--swatch-bg-size);
  color: var(--swatch-color);
  cursor: var(--swatch-cursor);
  
  ${({ isColorFilterSwatch, label }) =>
    `color: ${getReadableColorForColorSwatch(isColorFilterSwatch, label)};`}

  /* Responsive sizing using CSS custom properties */
  ${breakpoint('medium')} {
    width: var(--swatch-size-medium);
    height: var(--swatch-size-medium);
  }

  ${breakpoint('large')} {
    width: var(--swatch-size-large);
    height: var(--swatch-size-large);
  }

  /* Radio button styling */
  ${props => props['data-is-radio'] && css`
    &::after {
      position: absolute;
      content: '';
      width: var(--swatch-dimension-small);
      height: var(--swatch-dimension-small);
      border: var(--swatch-border-small) solid ${black};
      border-radius: 50%;
      opacity: var(--swatch-selected-opacity);
      transform: scale(var(--swatch-selected-scale));
      transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275),
        opacity 0.2s ease;
      z-index: -1;

      ${breakpoint('medium')} {
        width: var(--swatch-dimension-medium);
        height: var(--swatch-dimension-medium);
        border: var(--swatch-border-medium) solid ${black};
      }

      ${breakpoint('large')} {
        width: var(--swatch-dimension-large);
        height: var(--swatch-dimension-large);
        border: var(--swatch-border-large) solid ${black};
      }
    }
  `}
`;

export const Container = styled.span`
  display: inline-flex;
  align-items: center;
  vertical-align: top;
`;

export const Label = styled.label`
  display: inline-flex;
  align-items: center;
  vertical-align: top;

  ${({ inputType }) =>
    inputType &&
    inputType === 'radio' &&
    css`
      @media (hover: hover) {
        ${Input}:hover:not([disabled]) ~ ${Container} > ${Swatch} {
          &::after {
            opacity: 1;
            transform: scale(1);
          }
        }
      }

      ${Input}:active:not([disabled]) ~ ${Container} > ${Swatch} {
        &::after {
          opacity: 1;
          transform: scale(1);
        }
      }
    `}

  ${Input}:focus ~ ${Container} > ${Swatch} {
    ${switchProp('inputType', {
      radio: css`
        &::after {
          box-shadow: ${blue} 0px 0px 1px 1px;
        }
      `,
      checkbox: css`
        box-shadow: ${blue} 0px 0px 1px 1px;
      `
    })}
  }
`;

export const Name = styled.span`
  ${fontBody(16)};
  color: ${({ disabled }) => (disabled ? grey60 : black)};
  cursor: pointer;
  margin-left: 8px;

  ${({ isHidden }) => isHidden && hideVisually()}
`;
